/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { PreviewType, UnifiedCollectionType } from 'client-code/previews';

import { TIVO_SERVICES } from '../../../config';
import { ServicesClientImpl } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import { PreviewComponentAssets } from '../../../core/usecases';
import { DateTime } from '../../../core/utils/DateTime';
import { TrickPlayRestrictionType, VodEntitlementType, VodScreenType } from './VodPreviewProps';

/* eslint-disable class-methods-use-this */
export class VodPreviewFormatter {
    private previewInfo: any;

    private screenType: string;

    private oPreviewAssets: PreviewComponentAssets;

    public constructor(previewInfo: any, screenType: VodScreenType) {
        this.previewInfo = previewInfo;
        this.screenType = screenType;
        this.oPreviewAssets = new PreviewComponentAssets();
    }

    /* Returns localized entitlement text based on entitlement type */
    public getTrimmedDescriptionParts(): { visible: string; fading: string } {
        const desc = this.getDescription() || '';
        const maxChars = 200;

        if (desc.length <= maxChars) return { visible: desc, fading: '' };

        const cutIndex = desc.lastIndexOf(' ', maxChars);
        const trimmed = desc.slice(0, cutIndex);
        const fadeLength = 10;

        const visible = trimmed.slice(0, -fadeLength);
        const fading = trimmed.slice(-fadeLength);

        return { visible, fading };
    }

    public getEntitlementText(): string | null {
        const entitlementType = this.getEntitlement();
        switch (entitlementType) {
            // // FVOD  entitlement
            case VodEntitlementType.Free:
            case VodEntitlementType.FreeZeroPurchasable:
            case VodEntitlementType.FreeNoPurchase:
                return Locale.getString(LocaleKeys.VOD_ENTITLEMENT_FREE_TEXT);
            // SVOD entitlement
            case VodEntitlementType.Subscribed:
                return Locale.getString(LocaleKeys.VOD_ENTITLEMENT_INCLUDED_WITH_YOUR_SUBSCRIPTION);
            // SVOD unentitlement
            case VodEntitlementType.Subscribable:
                return Locale.getString(LocaleKeys.VOD_ENTITLEMENT_UPGRADE_TO_WATCH);
            // TVOD unentitlement
            case VodEntitlementType.Purchasable:
            case VodEntitlementType.Rentable:
            case VodEntitlementType.NotPurchaseable:
                return Locale.getString(LocaleKeys.VOD_ENTITLEMENT_NOT_AVAILABLE_FOR_PURCHASE);
            default:
                return null;
        }
    }

    private getEntitlement(): string | null {
        return this.previewInfo?.entitlement || null;
    }

    public getAvailabilityText(): string | null {
        const expirationRaw = this.previewInfo?.expirationTime;
        const entitlement = this.getEntitlement();

        if (!expirationRaw || entitlement == null) return null;

        const expirationDateTime = new DateTime(expirationRaw);
        const nowDateTime = new DateTime();
        const expirationDiffInMs = expirationDateTime.getTime() - nowDateTime.getTime();

        const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

        if (expirationDiffInMs <= 0 || expirationDiffInMs >= THIRTY_DAYS_MS) {
            return null;
        }

        // Skip message for Rentable or Purchasable
        if (
            entitlement === VodEntitlementType.Rentable ||
            entitlement === VodEntitlementType.Purchasable
        ) {
            return null;
        }

        const timeStr = expirationDateTime.to12HourClock();
        const dateStr = expirationDateTime.strftime('%M/%D');

        const isToday = expirationDateTime.isToday();
        const isTomorrow = expirationDateTime.isTomorrow();

        let template: string;
        if (isToday) {
            template = Locale.getString(LocaleKeys.VOD_AVAILABILITY_TODAY_SUBSTITUTION);
        } else if (isTomorrow) {
            template = Locale.getString(LocaleKeys.VOD_AVAILABILITY_TOMORROW_SUBSTITUTION);
        } else {
            template = Locale.getString(LocaleKeys.VOD_AVAILABILITY_UNTIL_SUBSTITUTION).replace(
                'DATE',
                dateStr,
            );
        }
        return template.replace('TIME', timeStr);
    }

    public getCombinedStatusMessage(): string {
        const entitlement = this.getEntitlementText();
        const availability = this.getAvailabilityText();

        if (entitlement && availability) {
            return `${entitlement} - ${availability}`;
        }
        if (entitlement) {
            return entitlement;
        }
        if (availability) {
            return availability;
        }
        return '';
    }

    private getSeparatorForRestrictionString(currentIndex: number, totalCount: number): string {
        if (currentIndex === 0) {
            return '';
        }
        if (currentIndex === totalCount - 1) {
            return ` ${Locale.getString(LocaleKeys.VOD_PREVIEW_TRICKPLAY_AND_TEXT)} `;
        }
        return ', ';
    }

    public getVodIcon(objectId: string): string {
        if (!objectId) {
            return '';
        }
        return `${ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost]}/images?objectid=${objectId}&imagetype=partnerSourceLogo&width=${32}&height=${32}`;
    }

    /** Returns trick-play restriction text based on previewInfo restrictions */
    public getVodTrickPlayRestriction(): string {
        const restrictions: TrickPlayRestrictionType[] =
            this.previewInfo?.trickPlayRestrictionList || [];
        const restrictionCount = restrictions.length;

        if (restrictionCount === 0) {
            return '';
        }

        let restrictionString = '';
        let count = 0;

        restrictions.forEach((restriction) => {
            if (restriction === TrickPlayRestrictionType.FAST_FORWARD) {
                restrictionString +=
                    this.getSeparatorForRestrictionString(count, restrictionCount) +
                    Locale.getString(LocaleKeys.VOD_PREVIEW_TRICKPLAY_FAST_FORWARD);
                count += 1;
            }

            if (restriction === TrickPlayRestrictionType.PAUSE) {
                restrictionString +=
                    this.getSeparatorForRestrictionString(count, restrictionCount) +
                    Locale.getString(LocaleKeys.VOD_PREVIEW_TRICKPLAY_PAUSE);
                count += 1;
            }

            if (restriction === TrickPlayRestrictionType.REWIND) {
                restrictionString +=
                    this.getSeparatorForRestrictionString(count, restrictionCount) +
                    Locale.getString(LocaleKeys.VOD_PREVIEW_TRICKPLAY_REWIND);
                count += 1;
            }
        });

        if (count === 1) {
            restrictionString += ` ${Locale.getString(LocaleKeys.VOD_PREVIEW_TRICKPLAY_IS_NOT_ALLOWED_TEXT)}`;
        } else if (count !== 0) {
            restrictionString += ` ${Locale.getString(LocaleKeys.VOD_PREVIEW_TRICKPLAY_NOT_ALLOWED_TEXT)}`;
        }

        return restrictionString;
    }

    private applyMiddleTruncation(base: string, suffix: string, maxLines: number): string {
        const maxLength = this.getMaxCharsForLines(maxLines);
        const combined = `${base}${suffix}`;
        if (combined.length <= maxLength) return combined;

        const keep = Math.floor((maxLength - suffix.length - 3) / 2);
        return `${base.slice(0, keep)}...${base.slice(-keep)}${suffix}`;
    }

    private applyPartialWordTruncation(text: string, maxLines: number): string {
        const maxLength = this.getMaxCharsForLines(maxLines);
        if (text.length <= maxLength) return text;
        const cut = text.slice(0, maxLength).split(' ').slice(0, -1).join(' ');
        return `${cut}`;
    }

    private getMaxCharsForLines(lines: number): number {
        const avgCharPerLine = 40;
        return lines * avgCharPerLine;
    }

    public getFormattedTitle(): string {
        const { previewType, title, seriesTitle, movieYear, isMixedContentStrip, isStripTitle } =
            this.previewInfo;

        let displayTitle = '';
        if (previewType === PreviewType.EpisodePreview) {
            if (isMixedContentStrip) {
                displayTitle = seriesTitle || Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE);
            } else {
                displayTitle = title || Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE);
            }
        } else if (previewType === PreviewType.MoviePreview) {
            const baseTitle = title || Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE);
            if (movieYear) {
                displayTitle = this.applyMiddleTruncation(baseTitle, ` (${movieYear})`, 2);
            } else {
                displayTitle = this.applyPartialWordTruncation(baseTitle, 2);
            }
        } else {
            displayTitle = title || Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE);
            displayTitle = this.applyPartialWordTruncation(displayTitle, 2);
        }
        if (isStripTitle) {
            return '';
        }
        return displayTitle;
    }

    public getTitle(): string {
        let title = this.previewInfo.title || Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE);
        if (
            this.previewInfo.previewType === PreviewType.EpisodePreview &&
            this.screenType === VodScreenType.VOD_GALLERY_SCREEN
        ) {
            title =
                this.previewInfo.seriesTitle || Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE);
        }
        if (
            this.previewInfo.previewType === PreviewType.MoviePreview &&
            this.previewInfo.movieYear
        ) {
            title += ` (${this.previewInfo.movieYear})`;
        }
        return title;
    }

    public getSeasonEpisodeTitle(): string {
        const season = this.previewInfo.seasonNumber ? `S${this.previewInfo.seasonNumber}` : '';
        const episode = this.previewInfo.episodeNumber ? `E${this.previewInfo.episodeNumber}` : '';
        const seaEp = `${season} ${episode}`.trim();
        const episodeTitle = this.previewInfo.episodeTitle || '';
        return `${seaEp} ${episodeTitle}`.trim();
    }

    public getDescription(): string {
        return (
            this.previewInfo.description ?? Locale.getString(LocaleKeys.DESCRIPTION_NOT_AVAILABLE)
        );
    }

    public getShowAttributes(): string {
        const attrs: string[] = [];
        if (this.previewInfo.hasCc) attrs.push('CC');
        if (this.previewInfo.hasSap) attrs.push('SAP');
        if (this.previewInfo.isThreeD) attrs.push('3D');
        else if (this.previewInfo.videoResolution) {
            attrs.push(this.previewInfo.videoResolution.toUpperCase());
        }

        return attrs.length ? ` (${attrs.join(', ')})` : '';
    }

    public getCast(lines: number = 3): string {
        if (this.previewInfo.previewType === PreviewType.EpisodePreview) {
            return this.previewInfo.guestInfo || '';
        }
        const match = /actor:(.+?)~/i.exec(this.previewInfo.creditString || '');
        if (!match) return '';
        const rawActors = match[1]
            .split(',')
            .map((name) => name.trim())
            .filter((name, index, self) => name && self.indexOf(name) === index);

        const CHARACTERS_PER_LINE = 45;
        const MAX_LINES = lines;
        const MAX_CHARACTERS = CHARACTERS_PER_LINE * MAX_LINES;
        const finalActors: string[] = [];
        let totalLength = 0;
        rawActors.some((actor) => {
            const actorWithSeparator = finalActors.length > 0 ? `, ${actor}` : actor;
            const actorLength = actorWithSeparator.length;

            if (totalLength + actorLength > MAX_CHARACTERS) {
                return true;
            }
            finalActors.push(actor);
            totalLength += actorLength;
            return false;
        });
        return finalActors.join(', ');
    }

    public getGenres = (): string => {
        return this.previewInfo?.genres?.join(', ') || this.previewInfo?.categoryLabel;
    };

    public getInternalRatings(): string {
        return this.previewInfo.internalRatingList;
    }

    public getDurationInfo(): string[] {
        const parts: string[] = [];
        if (
            this.previewInfo.unifiedCollectionType === UnifiedCollectionType.SeriesPreviewV2 &&
            this.screenType === VodScreenType.VOD_GALLERY_SCREEN
        ) {
            const currentStr = Locale.getString(LocaleKeys.VOD_PREVIEW_CURRENT);
            if (this.previewInfo.premierYear) {
                parts.push(
                    `${this.previewInfo.premierYear} - ${this.previewInfo.finaleYear || currentStr}`,
                );
            }
        } else {
            if (this.previewInfo.duration) {
                // Convert seconds → milliseconds
                const readableDuration = DateTime.convertToReadable(
                    this.previewInfo.duration * 1000,
                );
                parts.push(readableDuration);
            }
            if (
                [PreviewType.ProgramPreview, PreviewType.EpisodePreview].includes(
                    this.previewInfo.previewType,
                ) &&
                this.previewInfo.firstAiredDate &&
                this.previewInfo.displayAirDate !== false
            ) {
                const date = new Date(this.previewInfo.firstAiredDate);
                const today = new Date();
                const label = date >= today ? 'First airs:' : 'First aired:';
                parts.push(`${label} ${date.toLocaleDateString()}`);
            }
        }
        return parts;
    }

    public getStarRating(): number {
        return Math.min(this.previewInfo.starRating || 0, 10);
    }

    public getImageAssetUrl(type: any): string {
        const image = this.oPreviewAssets.getImageAssetUrlById(type);
        return image;
    }

    public getInfoImageAsset(): string {
        const image = this.oPreviewAssets.getImageAssetUrlById(
            PreviewComponentAssets.ImageAssets.BUTTON_INFO_ICON,
        );
        return image;
    }

    public getPreviewType(): string {
        return this.previewInfo.previewType;
    }

    public getTrickPlayRestrictions(): {
        imageAssetUrl?: string;
        stringAsset?: string;
    } {
        if (
            !this.previewInfo?.trickPlayRestrictionList ||
            this.previewInfo?.trickPlayRestrictionList.length === 0
        ) {
            return {};
        }
        const restrictionAssets = this.oPreviewAssets.getTrickplayRestrictionAssets(
            this.previewInfo?.trickPlayRestrictionList,
            this.previewInfo.isLive,
        );
        return restrictionAssets || {};
    }
}
